import { ClientCombobox } from "@/components/ClientCombobox";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { InternalLayout, PageHeader } from "@/components/InternalLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowLeftIcon as ArrowLeft, FloppyDiskIcon as FloppyDisk } from "@phosphor-icons/react";
import { RIP_TIPI, RIP_ACCESSORI } from "@/lib/riparazioni";

const today = () => new Date().toISOString().slice(0, 10);

const Section = ({ title, children }) => (
  <Card className="rounded-sm border-border shadow-none p-5">
    <h3 className="font-heading font-semibold text-sm uppercase tracking-wider mb-4 text-primary">{title}</h3>
    {children}
  </Card>
);

export default function RepairNew() {
  const nav = useNavigate();
  const [clients, setClients] = useState([]);
  const [useExisting, setUseExisting] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    data_ritiro: today(), cliente_id: "", cliente_nome: "", referente: "", telefono: "", email: "",
    tipologia_dispositivo: "notebook", marca: "", modello: "", seriale: "", asset_tag: "", password_dispositivo: "",
    problema_dichiarato: "", accessori_consegnati: [], condizioni_estetiche: "", note_reception: "",
  });
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => { api.get("/clients").then(({ data }) => setClients(data)); }, []);

  const toggleAcc = (a) => setForm((f) => ({
    ...f, accessori_consegnati: f.accessori_consegnati.includes(a)
      ? f.accessori_consegnati.filter((x) => x !== a) : [...f.accessori_consegnati, a],
  }));

  const onClientChange = (id) => {
    const c = clients.find((x) => x.id === id);
    setForm((f) => ({ ...f, cliente_id: id, cliente_nome: c?.ragione_sociale || "", telefono: c?.telefono || f.telefono, email: c?.email || f.email }));
  };

  const save = async () => {
    const payload = { ...form };
    if (useExisting) payload.cliente_nome = clients.find((c) => c.id === payload.cliente_id)?.ragione_sociale || "";
    else payload.cliente_id = "";
    if (!useExisting && !payload.cliente_nome.trim()) { toast.error("Inserisci il nome cliente"); return; }
    if (useExisting && !payload.cliente_id) { toast.error("Seleziona un cliente"); return; }
    if (!payload.problema_dichiarato.trim()) { toast.error("Il problema dichiarato è obbligatorio"); return; }
    if (!payload.tipologia_dispositivo) { toast.error("Seleziona la tipologia dispositivo"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/riparazioni", payload);
      toast.success(`Pratica ${data.numero_pratica} creata`);
      nav(`/riparazioni/${data.id}`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <InternalLayout>
      <div className="p-8 lg:p-12 max-w-4xl mx-auto">
        <div className="flex items-center gap-3 mb-2">
          <Button variant="ghost" size="icon" data-testid="rip-back-btn" onClick={() => nav(-1)} className="h-8 w-8"><ArrowLeft size={18} /></Button>
          <PageHeader title="Nuova riparazione" subtitle="Accettazione dispositivo alla reception" />
        </div>

        <div className="space-y-5">
          <Section title="Cliente">
            <div className="flex items-center gap-3 mb-4">
              <Switch checked={useExisting} onCheckedChange={setUseExisting} data-testid="rip-use-existing" />
              <Label className="text-sm">Cliente già in anagrafica</Label>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {useExisting ? (
                <div className="col-span-2">
                  <Label className="text-xs uppercase tracking-wider">Cliente *</Label>
                  <div className="mt-1"><ClientCombobox clients={clients} value={form.cliente_id} onChange={onClientChange} placeholder="Seleziona cliente" testid="rip-cliente-select" /></div>
                </div>
              ) : (
                <div className="col-span-2">
                  <Label className="text-xs uppercase tracking-wider">Nome cliente *</Label>
                  <Input data-testid="rip-cliente-nome" value={form.cliente_nome} onChange={(e) => upd("cliente_nome", e.target.value)} className="mt-1 rounded-sm" placeholder="Ragione sociale o nome" />
                </div>
              )}
              <div><Label className="text-xs uppercase tracking-wider">Referente</Label><Input value={form.referente} onChange={(e) => upd("referente", e.target.value)} className="mt-1 rounded-sm" /></div>
              <div><Label className="text-xs uppercase tracking-wider">Telefono</Label><Input value={form.telefono} onChange={(e) => upd("telefono", e.target.value)} className="mt-1 rounded-sm" /></div>
              <div className="col-span-2"><Label className="text-xs uppercase tracking-wider">Email</Label><Input value={form.email} onChange={(e) => upd("email", e.target.value)} className="mt-1 rounded-sm" /></div>
              <div><Label className="text-xs uppercase tracking-wider">Data ritiro *</Label><Input type="date" data-testid="rip-data-ritiro" value={form.data_ritiro} onChange={(e) => upd("data_ritiro", e.target.value)} className="mt-1 rounded-sm" /></div>
            </div>
          </Section>

          <Section title="Dispositivo">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs uppercase tracking-wider">Tipologia *</Label>
                <Select value={form.tipologia_dispositivo} onValueChange={(v) => upd("tipologia_dispositivo", v)}>
                  <SelectTrigger className="mt-1 rounded-sm capitalize" data-testid="rip-tipologia"><SelectValue /></SelectTrigger>
                  <SelectContent>{RIP_TIPI.map((t) => <SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs uppercase tracking-wider">Marca</Label><Input value={form.marca} onChange={(e) => upd("marca", e.target.value)} className="mt-1 rounded-sm" /></div>
              <div><Label className="text-xs uppercase tracking-wider">Modello</Label><Input data-testid="rip-modello" value={form.modello} onChange={(e) => upd("modello", e.target.value)} className="mt-1 rounded-sm" /></div>
              <div><Label className="text-xs uppercase tracking-wider">Seriale</Label><Input value={form.seriale} onChange={(e) => upd("seriale", e.target.value)} className="mt-1 rounded-sm" placeholder="consigliato" /></div>
              <div><Label className="text-xs uppercase tracking-wider">Asset tag</Label><Input value={form.asset_tag} onChange={(e) => upd("asset_tag", e.target.value)} className="mt-1 rounded-sm" /></div>
              <div className="col-span-2"><Label className="text-xs uppercase tracking-wider">Password dispositivo</Label><Input data-testid="rip-password" value={form.password_dispositivo} onChange={(e) => upd("password_dispositivo", e.target.value)} className="mt-1 rounded-sm" placeholder="PIN / password di accesso al dispositivo" /></div>
            </div>
          </Section>

          <Section title="Problema dichiarato">
            <Textarea data-testid="rip-problema" value={form.problema_dichiarato} onChange={(e) => upd("problema_dichiarato", e.target.value)} className="rounded-sm" rows={3} placeholder="Descrizione del guasto riferito dal cliente *" />
          </Section>

          <Section title="Accessori consegnati">
            <div className="flex flex-wrap gap-2">
              {RIP_ACCESSORI.map((a) => (
                <button key={a} type="button" onClick={() => toggleAcc(a)} data-testid={`rip-acc-${a}`}
                  className={`px-3 py-1.5 rounded-full text-sm border capitalize transition-colors ${
                    form.accessori_consegnati.includes(a) ? "bg-primary text-primary-foreground border-primary" : "bg-white border-border text-muted-foreground hover:border-primary/50"
                  }`}>{a}</button>
              ))}
            </div>
          </Section>

          <Section title="Condizioni estetiche">
            <Input value={form.condizioni_estetiche} onChange={(e) => upd("condizioni_estetiche", e.target.value)} className="rounded-sm" placeholder="Es. graffi sul coperchio, cornice crepata..." />
          </Section>

          <Section title="Note reception">
            <Textarea value={form.note_reception} onChange={(e) => upd("note_reception", e.target.value)} className="rounded-sm" rows={2} />
          </Section>

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => nav("/riparazioni")} className="rounded-sm">Annulla</Button>
            <Button data-testid="rip-save-btn" onClick={save} disabled={saving} className="rounded-sm gap-2"><FloppyDisk size={16} /> {saving ? "Salvataggio…" : "Registra e apri scheda"}</Button>
          </div>
        </div>
      </div>
    </InternalLayout>
  );
}
